import { useEffect, useRef } from "react";
import {
  Button,
  Cluster,
  Field,
  Icon,
  IconButton,
  Input,
  Kicker,
  Panel,
  Scrim,
  Select,
  Status,
  Subtitle,
  Title,
} from "@fwf/fwfa-design-system/react";
import { AGENT_PRESETS } from "@/lib/agent-configs";
import type {
  HarnessState,
  HarnessStatus,
  ClaudeProviderConfig,
  ToolPermissionPreset,
} from "@/types";

interface Props {
  visible: boolean;
  currentAgent: string | null;
  workingDirectory: string | null;
  harnessStates: HarnessState[];
  onClose: () => void;
  onChangeAgent: (agentId: string) => void;
  onChangeDirectory: () => void;
  onInstallAgent: (agentId: string) => Promise<boolean>;
  onStopAgent: () => void;
  onRefreshHarness: () => void;
  permissionPresets: ToolPermissionPreset[];
  onDeletePermissionPreset: (toolName: string) => void;
  claudeProvider: ClaudeProviderConfig | null;
  onClaudeProviderChange: (config: ClaudeProviderConfig) => void | Promise<void>;
  onRunAwsLogin: (profile?: string) => void;
  onRunTerminalCommand: (command: string) => void;
}

// Status is the DS's state signal: the dot carries the tone and the label stays
// calm. Only `error` is an alert, which lifts the tone into the label as well —
// the one harness state worth pulling the eye. The two resting states take no
// tone at all, so the dot falls back to muted ink rather than spending a colour
// on "nothing is happening".
const STATUS_DISPLAY: Record<
  HarnessStatus,
  { label: string; tone?: "success" | "danger"; live?: boolean; alert?: boolean }
> = {
  not_installed: { label: "Not installed" },
  installed: { label: "Installed" },
  running: { label: "Running", tone: "success", live: true },
  error: { label: "Error", tone: "danger", alert: true },
};

export function SettingsPanel({
  visible,
  currentAgent,
  workingDirectory,
  harnessStates,
  onClose,
  onChangeAgent,
  onChangeDirectory,
  onInstallAgent,
  onStopAgent,
  onRefreshHarness,
  permissionPresets,
  onDeletePermissionPreset,
  claudeProvider,
  onClaudeProviderChange,
  onRunAwsLogin,
  onRunTerminalCommand,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes and focus moves in on open, matching what DS Modal does for a
  // dialog. Modal itself is not usable here for the same reason AuthDialog gives:
  // it portals into document.body, which would tear this panel out of its stage
  // frame on /design-system and paint it over the whole page. Scrim + Panel keeps
  // the overlay where its render site puts it.
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  useEffect(() => {
    if (!visible) return;
    dialogRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  const handleInstall = async (presetId: string) => {
    // Close settings so the user can watch terminal/auth activity.
    onClose();
    await onInstallAgent(presetId);
    onRefreshHarness();
  };

  const handleStartAgent = (presetId: string) => {
    onChangeAgent(presetId);
  };

  const getHarnessStatus = (presetId: string): HarnessStatus => {
    return (
      harnessStates.find((h) => h.presetId === presetId)?.status ??
      "not_installed"
    );
  };

  const provider: ClaudeProviderConfig = claudeProvider ?? {
    provider: "anthropic",
    awsRegion: "",
    smallFastRegion: "",
    authMethod: "aws-profile",
    awsProfile: "",
    awsBedrockApiKey: "",
    awsAccessKeyId: "",
    awsSecretAccessKey: "",
    awsSessionToken: "",
  };

  const updateClaudeProvider = (patch: Partial<ClaudeProviderConfig>) => {
    void onClaudeProviderChange({
      ...provider,
      ...patch,
    });
  };

  const awsInstallCommand = window.freshvibe.platform === "win32"
    ? "winget install --id Amazon.AWSCLI -e"
    : "brew install awscli";

  return (
    <Scrim>
      {/* Dialog semantics sit on a wrapper rather than on Panel itself: the DS
          wrappers are typed ComponentPropsWithoutRef, so Panel cannot take the
          ref this needs to move focus. min-w-0 clears the grid item's automatic
          minimum size, since Scrim centres its child in a grid. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-dialog-title"
        tabIndex={-1}
        className="w-full min-w-0 max-w-lg"
      >
        <Panel className="animate-scale-in flex max-h-[85vh] flex-col gap-6 overflow-y-auto bg-surface-opaque shadow-float">
          <div className="flex items-center justify-between gap-3">
            <Title
              as="h2"
              id="settings-dialog-title"
              className="fwfa-title--component"
            >
              Settings
            </Title>
            <IconButton label="Close" compact onClick={onClose}>
              <Icon aria-hidden="true">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </Icon>
            </IconButton>
          </div>

          {/* Agent harnesses */}
          <section aria-labelledby="settings-harnesses">
            <div className="flex items-center justify-between">
              {/* mb-0 clears the Kicker's own bottom margin, which is rhythm for
                  a Kicker-over-Title pair and doubles up with the flex gap. */}
              <Kicker id="settings-harnesses" className="mb-0">
                Agent harnesses
              </Kicker>
              <IconButton
                label="Refresh harness status"
                compact
                title="Refresh status"
                onClick={onRefreshHarness}
              >
                <Icon aria-hidden="true">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"
                    />
                  </svg>
                </Icon>
              </IconButton>
            </div>

            <div className="mt-2 flex flex-col gap-1">
              {Object.entries(AGENT_PRESETS)
                .filter(([id]) => id !== "custom")
                .map(([id, preset]) => {
                  const hs = getHarnessStatus(id);
                  const display = STATUS_DISPLAY[hs];
                  const isActive = currentAgent === id;

                  return (
                    <div
                      key={id}
                      className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 transition-colors ${
                        isActive
                          ? "border border-line bg-surface-hover"
                          : "border border-transparent hover:bg-surface"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-display text-sm font-medium text-ink">
                          {preset.name}
                        </p>
                        <Status
                          tone={display.tone}
                          live={display.live}
                          alert={display.alert}
                          className="text-xs"
                        >
                          {display.label}
                        </Status>
                      </div>

                      <Cluster className="flex-none flex-nowrap gap-2">
                        {hs === "not_installed" && (
                          <Button
                            variant="primary"
                            compact
                            onClick={() => handleInstall(id)}
                          >
                            Install
                          </Button>
                        )}

                        {(hs === "installed" || hs === "error") && (
                          <Button
                            variant="primary"
                            compact
                            onClick={() => handleStartAgent(id)}
                          >
                            Start
                          </Button>
                        )}

                        {/* The one destructive control in the panel: stopping a
                            running agent kills the live session and whatever it
                            was mid-way through. */}
                        {hs === "running" && isActive && (
                          <Button variant="danger" compact onClick={onStopAgent}>
                            Stop
                          </Button>
                        )}

                        {hs === "running" && !isActive && (
                          <Button
                            variant="primary"
                            compact
                            onClick={() => handleStartAgent(id)}
                          >
                            Switch
                          </Button>
                        )}
                      </Cluster>
                    </div>
                  );
                })}
            </div>
          </section>

          {/* Working directory */}
          <section aria-labelledby="settings-directory">
            <Kicker id="settings-directory" className="mb-2">
              Working directory
            </Kicker>
            <Cluster className="flex-nowrap gap-2">
              <code className="min-w-0 flex-1 truncate rounded-sm border border-line-faint bg-field px-3 py-2 font-mono text-xs text-ink-muted">
                {workingDirectory || "Not set"}
              </code>
              <Button variant="secondary" compact onClick={onChangeDirectory}>
                Change
              </Button>
            </Cluster>
          </section>

          {/* Permission presets */}
          <section aria-labelledby="settings-presets">
            <Kicker id="settings-presets" className="mb-2">
              Permission presets
            </Kicker>
            {permissionPresets.length === 0 ? (
              <Subtitle className="fwfa-subtitle--sm text-ink-muted">
                No auto-approve presets saved yet. Use the permission dialog to
                save them per tool.
              </Subtitle>
            ) : (
              <div className="flex flex-col gap-1">
                {permissionPresets.map((preset) => (
                  <div
                    key={preset.toolName}
                    className="flex items-center justify-between gap-2 rounded-md border border-line-faint px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-display text-sm font-medium text-ink">
                        {preset.toolName}
                      </p>
                      <p className="text-xs text-ink-muted">Always approve</p>
                    </div>
                    {/* Ghost, not danger: dropping a preset re-tightens
                        permissions rather than destroying anything, and the
                        agent simply asks again next time. */}
                    <Button
                      variant="ghost"
                      compact
                      onClick={() => onDeletePermissionPreset(preset.toolName)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Claude provider */}
          <section aria-labelledby="settings-provider">
            <Kicker id="settings-provider" className="mb-2">
              Claude provider
            </Kicker>
            <div className="flex flex-col gap-3 rounded-md border border-line-faint p-3">
              <Field label="Provider">
                <Select
                  value={provider.provider}
                  onChange={(e) =>
                    updateClaudeProvider({
                      provider: e.target.value as ClaudeProviderConfig["provider"],
                    })
                  }
                >
                  <option value="anthropic">Anthropic (default Claude login)</option>
                  <option value="bedrock">AWS Bedrock</option>
                </Select>
              </Field>

              {provider.provider === "bedrock" && (
                <>
                  <Field label="AWS region">
                    <Input
                      value={provider.awsRegion}
                      onChange={(e) => updateClaudeProvider({ awsRegion: e.target.value })}
                      placeholder="us-east-1"
                    />
                  </Field>

                  <Field label="Auth method">
                    <Select
                      value={provider.authMethod}
                      onChange={(e) =>
                        updateClaudeProvider({
                          authMethod: e.target.value as ClaudeProviderConfig["authMethod"],
                        })
                      }
                    >
                      <option value="aws-profile">AWS profile / SSO login</option>
                      <option value="bedrock-api-key">Bedrock API key</option>
                      <option value="aws-access-keys">AWS access keys</option>
                    </Select>
                  </Field>

                  {provider.authMethod === "aws-profile" && (
                    <div className="flex flex-col gap-3">
                      <Field
                        label="AWS profile (optional)"
                        helper={`Install command: ${awsInstallCommand}`}
                      >
                        <Input
                          value={provider.awsProfile}
                          onChange={(e) => updateClaudeProvider({ awsProfile: e.target.value })}
                          placeholder="default"
                        />
                      </Field>
                      <Cluster className="justify-between gap-2">
                        <span className="font-mono text-xs text-ink-muted">
                          Run `aws sso login`
                        </span>
                        <Cluster className="flex-nowrap gap-2">
                          <Button
                            variant="secondary"
                            compact
                            onClick={() => onRunTerminalCommand(awsInstallCommand)}
                          >
                            Install CLI
                          </Button>
                          <Button
                            variant="primary"
                            compact
                            onClick={() => onRunAwsLogin(provider.awsProfile)}
                          >
                            Login in Terminal
                          </Button>
                        </Cluster>
                      </Cluster>
                    </div>
                  )}

                  {provider.authMethod === "bedrock-api-key" && (
                    <Field label="AWS_BEARER_TOKEN_BEDROCK">
                      <Input
                        type="password"
                        value={provider.awsBedrockApiKey}
                        onChange={(e) => updateClaudeProvider({ awsBedrockApiKey: e.target.value })}
                        placeholder="Enter Bedrock API key"
                      />
                    </Field>
                  )}

                  {provider.authMethod === "aws-access-keys" && (
                    <div className="flex flex-col gap-3">
                      <Field label="AWS_ACCESS_KEY_ID">
                        <Input
                          value={provider.awsAccessKeyId}
                          onChange={(e) => updateClaudeProvider({ awsAccessKeyId: e.target.value })}
                        />
                      </Field>
                      <Field label="AWS_SECRET_ACCESS_KEY">
                        <Input
                          type="password"
                          value={provider.awsSecretAccessKey}
                          onChange={(e) => updateClaudeProvider({ awsSecretAccessKey: e.target.value })}
                        />
                      </Field>
                      <Field label="AWS_SESSION_TOKEN (optional)">
                        <Input
                          type="password"
                          value={provider.awsSessionToken}
                          onChange={(e) => updateClaudeProvider({ awsSessionToken: e.target.value })}
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* About */}
          <p className="border-t border-line-faint pt-4 text-xs text-ink-muted">
            FreshVibe v{__BUILD_INFO__.version}+{__BUILD_INFO__.buildNumber} — {__BUILD_INFO__.branch}@{__BUILD_INFO__.commit}
          </p>
        </Panel>
      </div>
    </Scrim>
  );
}
